import {Chip} from '@dropbox/dig-components/chip';
import {FormRow} from '@dropbox/dig-components/form_row';
import {Keycap} from '@dropbox/dig-components/keycap';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Typeahead, WrapperRef} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AiSearchLine,
  ChecklistLine,
  NewsLine,
  PersonMultipleLine,
  SettingsLine,
  TeamLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import {IS_APPLE} from '@lexical/utils';
import {type AiSearchAskSource, logAiSearchInteraction} from 'analytics/aiSearchAnalytics';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {openAiSearchAndAskAtom} from 'atoms/aiSearch';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {showSearchAtom} from 'atoms/search';
import {Avatar, AvatarIcon} from 'components/DSYS/Avatar';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {getDocStatus, getStatusText} from 'components/DSYS/document/utils';
import {PersonHoverCard} from 'components/DSYS/HoverCard';
import {ROUTE_PATHS} from 'constant';
import {isQuestionShaped} from 'helpers/queryShape';
import {useDebouncedValue} from 'hooks/useDebounce';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {
  GlobalSearchResults,
  INITIAL_RESULTS,
  isCustom,
  isEmployee,
  isKnowledgeResult,
  isNewsPost,
  isPlanning,
  isTeamSearchResult,
  isWorkstream,
  isWorkstreamNavigation,
  MAX_RESULTS,
  PEOPLE_SEARCH_RANKING_MODE,
  useGlobalSearch,
} from 'hooks/useEmployee';
import {t} from 'i18next';
import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router';
import {
  workstreamsPath,
  workstreamsPillarPath,
  workstreamsPlacePath,
} from 'views/workstreams/page/workstreamsRoutes';
import {getWorkstreamDri} from 'views/workstreams/utils';

import {InstantAnswerCard} from './InstantAnswerCard';
import {KNOWLEDGE_ALL_LIMIT, KnowledgeResultRow} from './KnowledgeResultRow';
import styles from './PeopleSearch.module.css';
import {ResultIcon} from './ResultIcon';
import {SearchAskQuestionRow} from './SearchAskQuestionRow';
import {ASK_SUGGESTION_ROUTE, SearchEmptyState, SearchSectionTitle} from './SearchEmptyState';
import {useGlobalSearchAnalytics} from './useGlobalSearchAnalytics';
import {useKnowledgeSearch} from './useKnowledgeSearch';
import {useSpeculativeAsk} from './useSpeculativeAsk';

const baseFilters = ['all', 'people', 'teams', 'news', 'workstreams'] as const;
// Knowledge is behind the instant-answers flag and comes from its own endpoint.
const knowledgeFilters = [...baseFilters, 'knowledge'] as const;

/** Filters `/api/v1/search` understands. */
export type Filters = (typeof baseFilters)[number];
type SearchFilter = (typeof knowledgeFilters)[number];

export const RouteToIcon = {
  people: PersonMultipleLine,
  teams: TeamLine,
  workstream: WorkstreamsLine,
  workstream_scope: WorkstreamsLine,
  news: NewsLine,
  planning: ChecklistLine,
};

export interface RecentSearchItem {
  label: string;
  route: string;
  path: string;
  href?: string;
  scopeType?: string | null;
  scopeValue?: string | null;
}

export const isSupportedRecentSearchRoute = (route: string) => {
  return route in RouteToIcon;
};

export const buildRecentSearchHref = ({
  route,
  path,
  href,
  scopeType,
  scopeValue,
}: Pick<RecentSearchItem, 'route' | 'path' | 'href' | 'scopeType' | 'scopeValue'>):
  | string
  | null => {
  // Rebuilt from the scope, not the saved href: recents saved before the
  // Oct 26 /workstreams swap hold the old ?scope=…&value= links.
  if (route === 'workstream_scope' && scopeType) {
    const pillar = scopeValue?.trim();
    return scopeType === 'company'
      ? workstreamsPlacePath('company')
      : scopeType === 'pillar' && pillar
        ? workstreamsPillarPath(pillar)
        : workstreamsPath();
  }

  if (href) {
    return href;
  }

  if (route === 'workstream') {
    return ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', path);
  }

  if (!route) {
    return null;
  }

  return `/${route}/${path ?? ''}`;
};

export const normalizeRecentSearches = (recentSearches: RecentSearchItem[]) => {
  return recentSearches
    .filter((recentSearch) => {
      if (!recentSearch?.label || !recentSearch.route) {
        return false;
      }

      return isSupportedRecentSearchRoute(recentSearch.route);
    })
    .map((recentSearch) => {
      const normalizedHref = buildRecentSearchHref(recentSearch);
      if (recentSearch.route === 'workstream_scope' && !normalizedHref) {
        return null;
      }

      if (!normalizedHref || recentSearch.href === normalizedHref) {
        return recentSearch;
      }

      return {
        ...recentSearch,
        href: normalizedHref,
      };
    })
    .filter(Boolean) as RecentSearchItem[];
};

const groupedResults = (
  results: GlobalSearchResults[] = [],
  knowledgeResults: GlobalSearchResults[] = []
) => {
  const people = results.filter((item) => isEmployee(item)).map((item, idx) => ({idx, ...item}));
  const teams = results
    .filter((item) => isTeamSearchResult(item))
    .map((item, idx) => ({idx, ...item}));
  const knowledge = knowledgeResults.map((item, idx) => ({idx, ...item}));
  const workstreams = results
    .filter((item) => isWorkstream(item) || isWorkstreamNavigation(item))
    .map((item, idx) => ({idx, ...item}));
  const news = results.filter((item) => isNewsPost(item)).map((item, idx) => ({idx, ...item}));
  const planning = results.filter((item) => isPlanning(item)).map((item, idx) => ({idx, ...item}));

  // Knowledge goes right after the last person or team, so people stay first
  // and it leads only when nothing else matches ("401k", "concur").
  let insertAt = 0;
  results.forEach((item, index) => {
    if (isEmployee(item) || isTeamSearchResult(item)) insertAt = index + 1;
  });
  const all = [
    ...results.slice(0, insertAt),
    ...knowledgeResults.slice(0, KNOWLEDGE_ALL_LIMIT),
    ...results.slice(insertAt),
  ];

  return {
    all: all.map((item, idx) => ({idx, ...item})),
    people,
    teams,
    workstreams,
    news,
    planning,
    knowledge,
  };
};

const renderSubtitle = (result?: string | null) => {
  return (
    <Text size="small" color="faint">
      <Truncate lines={1} maxWidth={700}>
        {result}
      </Truncate>
    </Text>
  );
};

const renderTitle = (result?: string | null, hasSubtitle: boolean = true) => {
  return (
    <Box marginY={hasSubtitle ? '0' : '8'}>
      <Text isBold>
        <Truncate lines={1} maxWidth={700}>
          {result}
        </Truncate>
      </Text>
    </Box>
  );
};

const SearchAskActionRow = ({query, isOnlyOption}: {query: string; isOnlyOption: boolean}) => (
  <Box paddingTop="2">
    <Typeahead.Row
      key="company-os-ai-ask-action"
      value={{
        route: 'company_os_ai',
        path: query,
        label: query.trim() || t('ai_search_ask'),
        index: -1,
      }}
      style={{marginTop: 8, paddingRight: 4}}
      withLeftAccessory={<AvatarIcon src={CompanyAskGlyph} size="small" />}
      withTitle={
        <Split alignY="center" gap="8">
          <Split.Item width="fill" style={{minWidth: 0}}>
            <Box display="flex" alignItems="center" style={{gap: 6}}>
              <Text isBold style={{whiteSpace: 'nowrap'}}>
                {t('ai_search_ask')}
              </Text>
              <Text size="small" color="faint">
                •
              </Text>
              <Text color="faint">
                <Truncate lines={1} maxWidth={500}>
                  {query}
                </Truncate>
              </Text>
            </Box>
          </Split.Item>
          <Split.Item width="hug">
            <Keycap.Group
              display="inline"
              variant="outline"
              size="small"
              withAccessibleAdjacentLabel="and"
              keycaps={
                isOnlyOption
                  ? [{symbol: 'return', withAccessibleLabel: 'Enter'}]
                  : [
                      {
                        symbol: IS_APPLE ? 'command' : 'control',
                        withAccessibleLabel: IS_APPLE ? 'Command' : 'Control',
                      },
                      {symbol: 'return', withAccessibleLabel: 'Enter'},
                    ]
              }
            />
          </Split.Item>
        </Split>
      }
      className={styles.typeaheadRow}
    />
  </Box>
);

const SKELETON_WIDTHS: [100, 200][] = [
  [100, 200],
  [100, 200],
  [100, 200],
];

const SearchResultSkeletons = () => (
  <Box paddingTop="8" style={{opacity: 0.6}}>
    {SKELETON_WIDTHS.map(([titleW, subtitleW], i) => (
      <Box key={`skeleton-${i}`} marginBottom="8">
        <Typeahead.Row
          disabled
          withTitle={<Skeleton.Text width={titleW} />}
          withSubtitle={<Skeleton.Text width={subtitleW} />}
          withLeftAccessory={
            <Skeleton.Avatar size="large" style={{marginLeft: '5px', marginRight: '5px'}} />
          }
        />
      </Box>
    ))}
  </Box>
);

const renderWorkstreamNavigationSubtitle = (item: Extract<GlobalSearchResults, {id: string}>) => {
  if (!isWorkstreamNavigation(item)) return undefined;

  const subtitleParts =
    item.workstream_count >= 0 ? [t('workstreams_count', {count: item.workstream_count})] : [];

  return subtitleParts.length > 0 ? renderSubtitle(subtitleParts.join(' • ')) : undefined;
};

/** `tiled`: icons in avatar-sized tiles, like the people rows (the Ask relaunch). */
const renderTypeaheadRow = (item: GlobalSearchResults & {idx: number}, tiled: boolean) => {
  if (isKnowledgeResult(item)) {
    return <KnowledgeResultRow key={item.id} item={item} tiled={tiled} />;
  } else if (isEmployee(item)) {
    const title = renderTitle(item.name);

    return (
      <Typeahead.Row
        key={item.user_id}
        value={{
          route: 'people',
          path: item.ldap,
          index: item.idx,
          level: item.level,
          label: item.name,
        }}
        withTitle={
          <PersonHoverCard
            ldap={item.ldap}
            name={item.name}
            profilePhotoUrl={item.profile_photo_url}
            role={item.role}
          >
            <span style={{display: 'block', minWidth: 0}}>{title}</span>
          </PersonHoverCard>
        }
        withSubtitle={renderSubtitle(item.is_deactivated ? 'Deactivated' : item.role)}
        className={styles.typeaheadRow}
        withLeftAccessory={
          <Avatar user={item} size="large" isInactive={Boolean(item.is_deactivated)} />
        }
      />
    );
  } else if (isTeamSearchResult(item)) {
    return (
      <Typeahead.Row
        key={item.team_id}
        value={{route: 'teams', path: item.slug, index: item.idx, label: item.name}}
        withTitle={renderTitle(item.name, Boolean(item.description?.length))}
        withSubtitle={renderSubtitle(item.description)}
        className={styles.typeaheadRow}
        withLeftAccessory={<ResultIcon src={RouteToIcon['teams']} tiled={tiled} />}
      />
    );
  } else if (isCustom(item)) {
    return (
      <Typeahead.Row
        key={item.route}
        value={{route: item.route, index: item.idx, label: t(item.route)}}
        withTitle={renderTitle(t(item.route))}
        className={styles.typeaheadRow}
        withLeftAccessory={<ResultIcon src={SettingsLine} tiled={tiled} />}
      />
    );
  } else if (isWorkstreamNavigation(item)) {
    return (
      <Typeahead.Row
        key={item.id}
        value={{
          route: 'workstream_scope',
          path: item.id,
          index: item.idx,
          label: item.title,
          scopeType: item.scope_type,
          scopeValue: item.scope_value,
        }}
        withTitle={renderTitle(item.title)}
        withSubtitle={renderWorkstreamNavigationSubtitle(item)}
        className={styles.typeaheadRow}
        withLeftAccessory={<ResultIcon src={WorkstreamsLine} tiled={tiled} />}
      />
    );
  } else if (isWorkstream(item)) {
    return (
      <Typeahead.Row
        key={item.id}
        value={{route: 'workstream', path: item.id.toString(), index: item.idx, label: item.name}}
        withTitle={renderTitle(item.name)}
        withSubtitle={renderSubtitle(getWorkstreamDri(item)?.name)}
        className={styles.typeaheadRow}
        withLeftAccessory={<ResultIcon src={RouteToIcon['workstream']} tiled={tiled} />}
      />
    );
  } else if (isNewsPost(item)) {
    const date = new Date(item.created_at);
    const options = {year: 'numeric', month: 'long', day: 'numeric'};
    const formattedDate = date.toLocaleDateString('en-US', options as Intl.DateTimeFormatOptions);

    const categoryString = item.category[0] ? item.category[0].name : '';
    const categoryLabel = categoryString ? categoryString + ' • ' : '';

    return (
      <Typeahead.Row
        key={item.id}
        value={{route: 'news', path: item.id, index: item.idx, label: item.title}}
        withTitle={renderTitle(item.title)}
        withSubtitle={renderSubtitle(categoryLabel + formattedDate)}
        className={styles.typeaheadRow}
        withLeftAccessory={<ResultIcon src={RouteToIcon['news']} tiled={tiled} />}
      />
    );
  } else if (isPlanning(item)) {
    return (
      <Typeahead.Row
        key={item.id}
        value={{route: 'planning', path: item.id, index: item.idx, label: item.name}}
        withTitle={renderTitle(item.name)}
        withSubtitle={renderSubtitle(
          `${item.owner.name} • ${getStatusText(getDocStatus(item.status))}`
        )}
        className={styles.typeaheadRow}
        withLeftAccessory={<ResultIcon src={RouteToIcon['planning']} tiled={tiled} />}
      />
    );
  } else if (!item) {
    return (
      <Typeahead.Row
        key="undefined"
        disabled
        withTitle={<Skeleton.Text width={100} />}
        withSubtitle={<Skeleton.Text width={330} />}
        className={styles.typeaheadRow}
        withLeftAccessory={
          <Skeleton.Avatar size="large" style={{marginLeft: '5px', marginRight: '5px'}} />
        }
      />
    );
  }
};

export const recentSearchAtom = atomWithStorage('search', [] as RecentSearchItem[]);

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const wrapperRef = useRef<WrapperRef>();
  const [showSearch, setShowSearch] = useAtom(showSearchAtom);
  const openAiSearchAndAsk = useSetAtom(openAiSearchAndAskAtom);
  const {isAskRelaunchEnabled} = useAtomValue(growthbookCacheAtom);
  const [selectedFilter, setSelectedFilter] = useState<SearchFilter>('all');
  const [hasQueryParam, setHasQueryParam] = useState(false);
  const [recentSearches, setRecentSearches] = useAtom(recentSearchAtom);
  const [showMore, setShowMore] = useState(false);
  const rankingMode = PEOPLE_SEARCH_RANKING_MODE;
  // default input to the query param
  const [searchParams] = useSearchParams();
  const [userInputValue, setUserInputValue] = useState(
    searchParams.get('s')
      ? decodeURIComponent(
          (searchParams.get('s') || '').replace(/%(?![A-Fa-f0-9]{2})/g, '%25')
        ).trim()
      : ''
  );
  const debouncedInput = useDebouncedValue(userInputValue, 200);
  /** Immediate value for AI affordances; API search still uses `debouncedInput`. */
  const liveSearchInput = userInputValue.trim();
  const {
    data: searchResult,
    isLoading,
    isFetching,
  } = useGlobalSearch({
    input: debouncedInput.trim(),
    // The Knowledge filter keeps the "All" request (usually cached) so
    // switching back is instant.
    filter: selectedFilter === 'knowledge' ? 'all' : selectedFilter,
    showMore,
    includeWorkstreamScopes: true,
    shortFallbacks: isAskRelaunchEnabled,
  });
  const {data: knowledgeResult, isFetching: isKnowledgeFetching} = useKnowledgeSearch({
    input: debouncedInput,
    enabled: isAskRelaunchEnabled,
  });
  const filters: readonly SearchFilter[] = isAskRelaunchEnabled ? knowledgeFilters : baseFilters;
  const grouped = useMemo(
    () => groupedResults(searchResult, isAskRelaunchEnabled ? knowledgeResult : []),
    [searchResult, knowledgeResult, isAskRelaunchEnabled]
  );
  const filteredResults = grouped[selectedFilter];
  // With the relaunch, the empty box gets its own sections (Ask, Popular,
  // Suggested), and filters show only once you've typed and the results mix
  // types (people and teams, say); an applied filter stays so it can be cleared.
  const showEmptyState = isAskRelaunchEnabled && liveSearchInput.length === 0;
  const resultTypeCount = Object.entries(grouped).filter(
    ([group, items]) => group !== 'all' && items.length > 0
  ).length;
  const showFilterBar = isAskRelaunchEnabled
    ? liveSearchInput.length > 0 && (selectedFilter !== 'all' || resultTypeCount > 1)
    : grouped.all.length > 0;
  const resultsPending =
    isFetching || isKnowledgeFetching || liveSearchInput !== debouncedInput.trim();
  // Ask leads for questions, and for anything search came back empty on.
  const noSearchResults =
    liveSearchInput.length > 0 && filteredResults.length === 0 && !resultsPending;
  const askRowPromoted =
    isAskRelaunchEnabled && (isQuestionShaped(liveSearchInput) || noSearchResults);
  // With the relaunch there's no slim Ask row: a lookup ("Kyle") gets a full
  // one after its results, so the person is first and Enter opens them.
  const askRowTrails = isAskRelaunchEnabled && !askRowPromoted;
  // A topic that matches no one ("time off", "401k") can have an instant answer
  // too. Names always match people, so the card can't sit above the person.
  const lookupMatchesNoOne =
    searchResult !== undefined && grouped.people.length === 0 && grouped.teams.length === 0;
  const showInstantAnswer =
    isAskRelaunchEnabled &&
    liveSearchInput.length > 0 &&
    (isQuestionShaped(liveSearchInput) || lookupMatchesNoOne);
  // Questions start answering while typed; the Ask row shows how far along.
  const speculativeAsk = useSpeculativeAsk({
    query: liveSearchInput,
    enabled: isAskRelaunchEnabled && isQuestionShaped(liveSearchInput),
  });
  const {handOff: handOffSpeculativeAsk} = speculativeAsk;
  const askFromSearch = useCallback(
    (question: string, source: AiSearchAskSource) => {
      // Before search closes, which would cancel the turn it started.
      handOffSpeculativeAsk(question);
      openAiSearchAndAsk(question, source);
    },
    [handOffSpeculativeAsk, openAiSearchAndAsk]
  );
  const {
    markKeyboardSelection,
    trackFilterChange,
    trackFilterReset,
    trackResultSelection,
    trackShowMore,
  } = useGlobalSearchAnalytics({
    entryPoint: showSearch,
    query: debouncedInput,
    hasLiveQuery: Boolean(liveSearchInput),
    activeFilter: selectedFilter,
    results: filteredResults,
    resultsPending,
    showMore,
    rankingMode,
    askRowPromoted,
  });

  const handleSelection = useCallback(
    ({
      route,
      path,
      label,
      level,
      index = 0,
      scopeType,
      scopeValue,
      href,
      suggestionKind,
    }: {
      route: string;
      path: string;
      label: string;
      level?: string;
      index?: number;
      scopeType?: string | null;
      scopeValue?: string | null;
      href?: string;
      suggestionKind?: string;
    }) => {
      if (route === ASK_SUGGESTION_ROUTE) {
        logAiSearchInteraction({
          action: 'suggestion_click',
          source: 'search',
          suggestionIndex: index,
          suggestionKind,
        });
        openAiSearchAndAsk(path, 'search_suggestion');
        return;
      }

      if (route === 'company_os_ai') {
        askFromSearch(path, 'search_typeahead_row');
        return;
      }

      trackResultSelection({route, path, index});

      const resolvedHref = buildRecentSearchHref({
        route,
        path,
        href,
        scopeType,
        scopeValue,
      });
      // Knowledge isn't a recent-search route; its links can change in admin.
      if (route !== 'knowledge') {
        const recentSearchesCopy = recentSearches.filter(
          (recent) => !(recent.route === route && recent.path === path)
        );
        recentSearchesCopy.unshift({
          label,
          route,
          path,
          href: resolvedHref ?? undefined,
          scopeType,
          scopeValue,
        });
        if (recentSearchesCopy.length >= 8) {
          recentSearchesCopy.length = 8;
        }
        setRecentSearches(recentSearchesCopy);
      }

      analyticsLogger().logEvent('SEARCH_SELECTED', {
        route,
        filter: selectedFilter,
        results: filteredResults.length,
        input: debouncedInput.length,
        level,
        index,
        ranking_mode: rankingMode,
        results_loaded: !resultsPending,
      });
      setShowSearch(false);
      if (resolvedHref && /^https?:\/\//.test(resolvedHref)) {
        window.open(resolvedHref, '_blank', 'noopener,noreferrer');
        return;
      }
      if (resolvedHref) {
        navigate(resolvedHref, {
          state: {source: showSearch === 'queryparam' ? 'queryparam' : 'search'},
        });
        return;
      }
    },
    [
      debouncedInput.length,
      filteredResults.length,
      navigate,
      recentSearches,
      resultsPending,
      selectedFilter,
      setRecentSearches,
      setShowSearch,
      showSearch,
      askFromSearch,
      openAiSearchAndAsk,
      rankingMode,
      trackResultSelection,
    ]
  );

  const loadFirstResult = useCallback(() => {
    const firstResult = filteredResults[0];
    const canSelectFirstResult =
      isEmployee(firstResult) ||
      isTeamSearchResult(firstResult) ||
      isWorkstream(firstResult) ||
      isWorkstreamNavigation(firstResult) ||
      isNewsPost(firstResult) ||
      isKnowledgeResult(firstResult);

    if (!canSelectFirstResult) return;

    markKeyboardSelection();
    if (isEmployee(firstResult)) {
      handleSelection({
        route: 'people',
        path: firstResult.ldap ?? firstResult.user_id,
        level: firstResult.level ?? undefined,
        label: firstResult.name,
      });
    } else if (isTeamSearchResult(firstResult)) {
      handleSelection({
        route: 'teams',
        path: firstResult.slug ?? '',
        label: firstResult.name ?? '',
      });
    } else if (isWorkstream(firstResult)) {
      handleSelection({
        route: 'workstream',
        path: firstResult.id.toString(),
        label: firstResult.name,
      });
    } else if (isWorkstreamNavigation(firstResult)) {
      handleSelection({
        route: 'workstream_scope',
        path: firstResult.id,
        label: firstResult.title,
        scopeType: firstResult.scope_type,
        scopeValue: firstResult.scope_value,
      });
    } else if (isNewsPost(firstResult)) {
      handleSelection({
        route: 'news',
        path: firstResult.id.toString(),
        label: firstResult.title,
      });
    } else if (isKnowledgeResult(firstResult)) {
      handleSelection({
        route: 'knowledge',
        path: firstResult.id,
        href: firstResult.url,
        label: firstResult.title,
      });
    }
  }, [filteredResults, handleSelection, markKeyboardSelection]);

  useEffect(() => {
    if (searchParams.get('s')) {
      setHasQueryParam(true);
    }
    analyticsLogger().logEvent('SEARCH_SHOWN', {source: showSearch});
    // Ignore changes to the search param. We only want to update the input value the first time it loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasQueryParam && filteredResults.length === 1) {
      loadFirstResult();
    }
  }, [hasQueryParam, filteredResults, loadFirstResult]);

  useEffect(() => {
    const normalizedRecentSearches = normalizeRecentSearches(recentSearches);

    if (JSON.stringify(normalizedRecentSearches) !== JSON.stringify(recentSearches)) {
      setRecentSearches(normalizedRecentSearches);
    }
  }, [recentSearches, setRecentSearches]);

  useEffect(() => {
    if (liveSearchInput.length === 0) {
      trackFilterReset();
      setSelectedFilter('all');
    }
  }, [liveSearchInput.length, trackFilterReset]);

  useEffect(() => {
    analyticsLogger().logEvent('SEARCH_FILTER_RESULTS', {
      filter: selectedFilter,
      results: filteredResults.length,
      input: debouncedInput.length,
      ranking_mode: rankingMode,
    });
  }, [selectedFilter, filteredResults.length, debouncedInput.length, rankingMode]);

  const setFilterAndLog = (filter: SearchFilter) => {
    analyticsLogger().logEvent('SEARCH_FILTER_SELECTED', {
      filter,
      results: filteredResults.length,
      input: debouncedInput.length,
    });
    trackFilterChange(filter);
    setSelectedFilter(filter);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInputValue(e.currentTarget.value);
  };

  const handleTab = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      wrapperRef.current?.focusTrigger();

      const currentIndex = filters.indexOf(selectedFilter);
      const nextIndex = e.shiftKey
        ? (currentIndex + filters.length - 1) % filters.length
        : (currentIndex + 1) % filters.length;
      setFilterAndLog(filters[nextIndex]);
    }
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') {
      return;
    }

    // Plain Enter asks AI when the query reads like a question (the Ask row is
    // first), or once results have settled and the Ask row is the sole option.
    // While results are still loading we don't yet know whether any results
    // exist, so a lookup keeps requiring Cmd/Ctrl + Enter to ask.
    const onlyAiRowShowing = filteredResults.length === 0 && !resultsPending;
    const askAiViaKeyboard =
      userInputValue.trim() && (e.metaKey || e.ctrlKey || onlyAiRowShowing || askRowPromoted);

    if (askAiViaKeyboard) {
      e.preventDefault();
      askFromSearch(userInputValue, 'search_keyboard');
      return;
    }
    if (filteredResults?.[0]) {
      loadFirstResult();
    }
  };

  useDocumentTitle(
    t('searching') + (userInputValue.length > 0 ? ' "' + userInputValue + '"' : '...'),
    true
  );

  return (
    <div onKeyDown={handleTab}>
      <div className={styles.searchContainer} style={{borderRadius: '10px'}}>
        <Typeahead.Wrapper
          wrapperRef={wrapperRef}
          onSelection={handleSelection}
          style={{width: '100%'}}
          omitOverlay
        >
          {({getTriggerProps, getContentProps}) => (
            <>
              <Box style={{marginBottom: -16}}>
                <FormRow>
                  <TextInput
                    {...getTriggerProps({
                      onChange: onInputChange,
                      onKeyDown: handleEnter,
                    })}
                    placeholder={t('search_global_all').toString()}
                    value={userInputValue}
                    withLeftAccessory={
                      <UIIcon
                        size="large"
                        src={AiSearchLine}
                        className={atoms({color: 'Text Subtle'})}
                      />
                    }
                    autoFocus
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    style={{fontSize: 18, paddingBottom: -16}}
                  />
                </FormRow>
              </Box>
              {liveSearchInput.length !== 0 &&
                (askRowPromoted ? (
                  // The same gap off the input as between result rows.
                  <Box paddingTop="12">
                    <SearchAskQuestionRow
                      query={liveSearchInput}
                      progress={speculativeAsk.progress}
                    />
                  </Box>
                ) : askRowTrails ? null : (
                  <SearchAskActionRow
                    query={liveSearchInput}
                    isOnlyOption={filteredResults.length === 0 && !resultsPending}
                  />
                ))}

              {showInstantAnswer && (
                <InstantAnswerCard
                  query={liveSearchInput}
                  isOnlyOption={filteredResults.length === 0 && !resultsPending}
                />
              )}

              {showFilterBar && (
                <FilterBar
                  filters={filters}
                  selectedFilter={selectedFilter}
                  setFilter={setFilterAndLog}
                  compact={isAskRelaunchEnabled}
                />
              )}

              <Typeahead.Container
                {...getContentProps()}
                triggerOffset={0}
                open
                className={styles.typeaheadResultsContainer}
                // The relaunch's empty box always has its sections, even with nothing to suggest.
                isEmptyQuery={filteredResults.length === 0 && !showEmptyState}
                emptyPrompt={
                  liveSearchInput.length > 0 ? (
                    <Box style={{width: '100%', boxSizing: 'border-box'}}>
                      {resultsPending ? (
                        <SearchResultSkeletons />
                      ) : askRowPromoted ? null : (
                        <Typeahead.Prompt>
                          <Text color="faint">{t('no_dropboxer')}</Text>
                        </Typeahead.Prompt>
                      )}
                    </Box>
                  ) : (
                    <Typeahead.Prompt>
                      <Text color="faint">{t('no_dropboxer')}</Text>
                    </Typeahead.Prompt>
                  )
                }
                loading={isLoading && !(liveSearchInput.length > 0)}
              >
                {showEmptyState && <SearchEmptyState />}
                {showEmptyState && filteredResults.length > 0 && (
                  <SearchSectionTitle>{t('search_section_suggested')}</SearchSectionTitle>
                )}
                <Typeahead.Results
                  size="large"
                  results={filteredResults}
                  renderRow={(item) => renderTypeaheadRow(item, isAskRelaunchEnabled)}
                  initialResults={showMore ? MAX_RESULTS : INITIAL_RESULTS}
                  maxResults={MAX_RESULTS}
                  showMoreOnClick={() => {
                    setShowMore(true);
                    trackShowMore();
                    analyticsLogger().logEvent('SEARCH_SHOW_MORE_CLICKED', {
                      filter: selectedFilter,
                      results: filteredResults.length,
                      input: debouncedInput.length,
                      ranking_mode: rankingMode,
                    });
                  }}
                  showMoreButtonText={t('show_more')}
                />
                {askRowTrails && liveSearchInput.length > 0 && (
                  // Its own results section, so it lines up with the rows above.
                  <Typeahead.Results
                    size="large"
                    results={[liveSearchInput]}
                    renderRow={(query) => <SearchAskQuestionRow query={query} trailing />}
                  />
                )}
              </Typeahead.Container>
            </>
          )}
        </Typeahead.Wrapper>
      </div>
    </div>
  );
};

const FilterBar = ({
  filters,
  selectedFilter,
  setFilter,
  compact = false,
}: {
  filters: readonly SearchFilter[];
  selectedFilter: SearchFilter;
  setFilter: (filter: SearchFilter) => void;
  compact?: boolean;
}) => {
  return (
    <Split paddingTop="12" paddingBottom="0" alignY="center">
      <Split.Item width="fill">
        <Split gap="8" wrap="wrap">
          {filters.map((filter) => (
            <Split.Item key={filter}>
              <Chip
                size={compact ? 'small' : undefined}
                isSelected={filter === selectedFilter}
                onClick={() => setFilter(filter)}
                {...withShade({})}
              >
                {t(filter)}
              </Chip>
            </Split.Item>
          ))}
        </Split>
      </Split.Item>
    </Split>
  );
};
